import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Ticket, Search, AlertCircle, CheckCircle2, Clock, X, UserPlus, Settings, ChevronRight, Bug, Loader2, IndianRupee, MapPin, History } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS_META = {
  open: { cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Open' },
  assigned: { cls: 'bg-indigo-100 text-indigo-700 border-indigo-300', label: 'Assigned' },
  in_progress: { cls: 'bg-blue-100 text-blue-700 border-blue-300', label: 'In Progress' },
  awaiting_payment: { cls: 'bg-orange-100 text-orange-800 border-orange-300', label: 'Awaiting Payment' },
  resolved: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'Resolved' },
  closed: { cls: 'bg-slate-200 text-slate-700 border-slate-300', label: 'Closed' },
  force_closed: { cls: 'bg-rose-200 text-rose-800 border-rose-300', label: 'Force-Closed' },
};

export default function ServiceTicketsAdminTab({ api, isSuper }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openTicket, setOpenTicket] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [err, setErr] = useState('');

  const minusDays = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const today = () => new Date().toISOString().slice(0, 10);
  const range = useMemo(() => {
    if (dateFilter === 'today') return { date_from: today(), date_to: today() };
    if (dateFilter === '7d') return { date_from: minusDays(7), date_to: today() };
    if (dateFilter === '30d') return { date_from: minusDays(30), date_to: today() };
    if (dateFilter === 'custom') return { date_from: dateFrom, date_to: dateTo };
    return {};
  }, [dateFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { ...range };
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await api('GET', '/admin/service-tickets', { params });
      setRows(r.data?.items || []);
      setCounts(r.data?.counts_by_status || {});
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api, q, statusFilter, range]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="service-tickets-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Ticket size={20} className="text-amber-700" />
          <h2 className="text-xl font-bold">Service Tickets</h2>
          <span className="text-xs text-slate-500">— Customer complaints, raised against bookings & pests.</span>
        </div>
        {isSuper && (
          <Button onClick={() => setShowSettings(true)} variant="outline" size="sm" data-testid="tkt-settings-btn"><Settings size={14} className="mr-1.5" /> Charge Settings</Button>
        )}
      </div>

      {/* Status quick pills */}
      <div className="flex flex-wrap gap-1.5" data-testid="tkt-status-filter">
        {[['', 'All'], ['open', 'Open'], ['assigned', 'Assigned'], ['in_progress', 'In Progress'], ['awaiting_payment', 'Awaiting Payment'], ['resolved', 'Resolved'], ['force_closed', 'Closed']].map(([k, l]) => {
          const n = k ? counts[k] : Object.values(counts).reduce((a, b) => a + b, 0);
          return (
            <button key={k} onClick={() => setStatusFilter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${statusFilter === k ? 'bg-amber-600 text-white' : 'bg-white border border-slate-300 text-slate-700'}`} data-testid={`tkt-st-${k || 'all'}`}>
              {l} {n > 0 && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === k ? 'bg-white/25' : 'bg-slate-100'}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ticket #, booking ID, name, mobile, email, or pest…" className="pl-9 h-10" data-testid="tkt-search" />
        </div>
        <div className="flex flex-wrap gap-1">
          {[['all', 'All'], ['today', 'Today'], ['7d', '7d'], ['30d', '30d'], ['custom', 'Custom']].map(([k, l]) => (
            <button key={k} onClick={() => setDateFilter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${dateFilter === k ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-testid={`tkt-date-${k}`}>{l}</button>
          ))}
          {dateFilter === 'custom' && (<>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-auto" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-auto" />
          </>)}
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

      {loading ? <div className="text-center py-10 text-slate-400">Loading…</div> :
        rows.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No tickets match.</div> :
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          {rows.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.open;
            return (
              <div key={t.id} onClick={() => setOpenTicket(t)} className="border-t first:border-t-0 border-slate-100 px-3 py-3 hover:bg-amber-50/40 cursor-pointer flex items-start gap-3" data-testid={`tkt-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-slate-700">{t.ticket_no}</span>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                    {t.is_warranty_claim && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Warranty</span>}
                    {t.priority === 'high' && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">High</span>}
                  </div>
                  <div className="text-sm font-semibold mt-1 text-slate-900">{t.customer_name} · <Bug size={11} className="inline" /> {t.pest_label || t.pest_type}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">{t.description}</div>
                  <div className="text-[11px] text-slate-500 mt-1">📞 {t.customer_phone} · 🧾 {t.booking_code} · {fmtDateTime(t.created_at)}{t.assigned_to_name && <> · 👷 {t.assigned_to_name}</>}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-base font-bold text-emerald-700">{fmtMoney(t.charge_amount)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">{t.charge_status}</div>
                </div>
                <ChevronRight size={14} className="text-slate-400 mt-2 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      }

      {openTicket && <TicketDrawer api={api} ticket={openTicket} onClose={() => setOpenTicket(null)} onChanged={(updated) => { setOpenTicket(updated); load(); }} isSuper={isSuper} />}
      {showSettings && <TicketSettingsModal api={api} onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// =====================================================
// TICKET DRAWER — right side, full management
// =====================================================
function TicketDrawer({ api, ticket, onClose, onChanged, isSuper }) {
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [assigneeId, setAssigneeId] = useState(ticket.assigned_to || '');
  const [charge, setCharge] = useState(ticket.charge_amount || 0);
  const [adminNotes, setAdminNotes] = useState(ticket.admin_notes || '');
  const [priority, setPriority] = useState(ticket.priority || 'medium');
  const [closeReason, setCloseReason] = useState('');
  const [showCloseBox, setShowCloseBox] = useState(false);

  useEffect(() => {
    api('GET', '/admin/employees').then(r => setEmployees((r.data || []).filter(e => e.status === 'active'))).catch(() => {});
  }, [api]);

  const meta = STATUS_META[ticket.status] || STATUS_META.open;

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api('PATCH', `/admin/service-tickets/${ticket.id}`, { charge_amount: Number(charge), admin_notes: adminNotes, priority });
      onChanged(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const assign = async () => {
    if (!assigneeId) { setErr('Pick an employee'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api('POST', `/admin/service-tickets/${ticket.id}/assign`, { employee_id: assigneeId });
      onChanged(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const forceClose = async () => {
    if (!closeReason.trim()) { setErr('Reason is required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api('POST', `/admin/service-tickets/${ticket.id}/close`, { reason: closeReason.trim() });
      onChanged(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="tkt-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-amber-700 text-white flex items-center justify-between">
          <div>
            <div className="text-xs opacity-90">Ticket</div>
            <div className="text-lg font-bold font-mono">{ticket.ticket_no}</div>
            <div className="text-xs opacity-90 mt-0.5"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span></div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="tkt-drawer-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}

          <Box title="Customer">
            <div className="text-sm font-bold">{ticket.customer_name}</div>
            <div className="text-xs text-slate-600">📞 {ticket.customer_phone}{ticket.customer_email && <> · ✉ {ticket.customer_email}</>}</div>
            {ticket.customer_address && <div className="text-xs text-slate-600 mt-0.5"><MapPin size={11} className="inline mr-1" />{ticket.customer_address}</div>}
            <div className="text-xs text-slate-500 mt-1">Linked Booking: <b>{ticket.booking_code}</b></div>
          </Box>

          <Box title="Complaint">
            <div className="text-xs uppercase text-slate-500 font-bold flex items-center gap-1"><Bug size={11} /> {ticket.pest_label || ticket.pest_type}</div>
            <div className="text-sm mt-1">{ticket.description}</div>
            {ticket.is_warranty_claim && <div className="mt-1.5 text-[11px] inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">✅ Auto-warranty claim — within service warranty window</div>}
          </Box>

          {/* Charge + Admin controls */}
          <Box title="Service Charge & Notes">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Charge (₹) — Super Admin can override</Label>
                <Input type="number" min="0" step="50" value={charge} onChange={(e) => setCharge(e.target.value)} className="h-9 mt-1" data-testid="tkt-charge-input" />
                <div className="text-[10px] text-slate-500 mt-1">Status: <b className="uppercase">{ticket.charge_status}</b></div>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="tkt-priority">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
            </div>
            <Label className="text-xs mt-2 block">Admin Notes (internal)</Label>
            <textarea rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="w-full p-2 mt-1 rounded-md border border-slate-300 text-xs" data-testid="tkt-admin-notes" />
            <Button onClick={save} disabled={busy} size="sm" className="mt-2 bg-amber-700 hover:bg-amber-800" data-testid="tkt-save">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} Save Changes</Button>
          </Box>

          {/* Assign */}
          {ticket.status !== 'force_closed' && ticket.status !== 'resolved' && (
            <Box title="Assign Technician">
              <div className="flex gap-2">
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className="flex-1 h-9 rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="tkt-assignee-select">
                  <option value="">— Choose technician —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
                </select>
                <Button onClick={assign} disabled={busy || !assigneeId} size="sm" className="bg-indigo-700 hover:bg-indigo-800" data-testid="tkt-assign-go"><UserPlus size={13} className="mr-1" /> Assign</Button>
              </div>
            </Box>
          )}

          {/* Resolution */}
          {ticket.resolution_notes && (
            <Box title="Resolution Notes" tone="emerald">
              <div className="text-sm">{ticket.resolution_notes}</div>
              <div className="text-[10px] text-slate-500 mt-1">By {ticket.resolved_by} on {fmtDateTime(ticket.resolved_at)}</div>
            </Box>
          )}

          {/* Force-close */}
          {ticket.status !== 'force_closed' && (
            <Box title="Danger Zone" tone="rose">
              {!showCloseBox ? (
                <Button onClick={() => setShowCloseBox(true)} size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="tkt-force-close-btn">Force-Close Ticket</Button>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs">Reason (visible to customer) *</Label>
                  <textarea rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} className="w-full p-2 rounded-md border border-rose-300 text-xs" data-testid="tkt-close-reason" />
                  <div className="flex gap-2">
                    <Button onClick={forceClose} disabled={busy} size="sm" className="bg-rose-700 hover:bg-rose-800" data-testid="tkt-close-confirm">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} Force-Close Now</Button>
                    <Button onClick={() => setShowCloseBox(false)} size="sm" variant="outline">Cancel</Button>
                  </div>
                </div>
              )}
            </Box>
          )}

          {/* Status history */}
          {(ticket.status_history || []).length > 0 && (
            <Box title={<><History size={12} className="inline mr-1" /> History</>}>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {ticket.status_history.slice().reverse().map((h, i) => (
                  <div key={i} className="bg-slate-50 rounded p-2 text-xs">
                    <div className="font-semibold capitalize">{(h.action || '').replace(/_/g, ' ')}</div>
                    {h.note && <div className="text-slate-600">{h.note}</div>}
                    <div className="text-[10px] text-slate-500 mt-0.5">{h.actor} · {fmtDateTime(h.ts)}</div>
                  </div>
                ))}
              </div>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
}

function Box({ title, tone = 'slate', children }) {
  const tones = { slate: 'bg-white border-slate-200', emerald: 'bg-emerald-50 border-emerald-200', rose: 'bg-rose-50 border-rose-200', amber: 'bg-amber-50 border-amber-200' };
  return (
    <div className={`rounded-lg p-3 border ${tones[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">{title}</div>
      {children}
    </div>
  );
}

// =====================================================
// CHARGE SETTINGS MODAL — Super Admin only
// =====================================================
function TicketSettingsModal({ api, onClose }) {
  const [data, setData] = useState({ default_charge: 500, pest_overrides: {}, free_warranty_days: 30, enable_auto_warranty_claim: true });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [newPest, setNewPest] = useState('');
  const [newAmt, setNewAmt] = useState('');

  useEffect(() => {
    api('GET', '/admin/service-ticket-settings').then(r => { setData(r.data); setLoading(false); }).catch(e => { setErr(e.response?.data?.detail || e.message); setLoading(false); });
  }, [api]);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api('PUT', '/admin/service-ticket-settings', data);
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const addOverride = () => {
    if (!newPest.trim() || !newAmt) return;
    setData(d => ({ ...d, pest_overrides: { ...d.pest_overrides, [newPest.trim()]: Number(newAmt) } }));
    setNewPest(''); setNewAmt('');
  };
  const removeOverride = (k) => setData(d => { const c = { ...d.pest_overrides }; delete c[k]; return { ...d, pest_overrides: c }; });

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="tkt-settings-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden">
        <div className="px-5 py-3.5 bg-indigo-700 text-white flex items-center justify-between">
          <div><div className="text-xs opacity-90">Super Admin</div><div className="font-bold">Service Ticket — Charge Settings</div></div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading ? <div className="text-center py-6 text-slate-400">Loading…</div> : <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Default Charge (₹)</Label>
                <Input type="number" min="0" step="50" value={data.default_charge} onChange={(e) => setData({ ...data, default_charge: Number(e.target.value) })} className="h-10 mt-1" data-testid="tkt-set-default" />
              </div>
              <div>
                <Label className="text-xs">Free-Warranty Days <span className="text-[10px] text-slate-400">(complaint within these days = free)</span></Label>
                <Input type="number" min="0" value={data.free_warranty_days} onChange={(e) => setData({ ...data, free_warranty_days: Number(e.target.value) })} className="h-10 mt-1" data-testid="tkt-set-warr-days" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!data.enable_auto_warranty_claim} onChange={(e) => setData({ ...data, enable_auto_warranty_claim: e.target.checked })} className="w-4 h-4" data-testid="tkt-set-auto-warr" />
              <span className="text-sm">Auto-set charge to ₹0 if complaint is within warranty window</span>
            </label>

            <div className="border-t pt-3">
              <Label className="text-xs font-bold uppercase">Per-Pest Charge Overrides</Label>
              <div className="text-[10px] text-slate-500 mb-2">Termite typically costs more; Cockroach is cheaper. Override per pest:</div>
              <div className="space-y-1">
                {Object.entries(data.pest_overrides).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5 text-sm" data-testid={`tkt-ov-${k}`}>
                    <span className="flex-1 capitalize">{k}</span>
                    <span className="font-bold text-emerald-700">{fmtMoney(v)}</span>
                    <button onClick={() => removeOverride(k)} className="text-rose-500 hover:text-rose-700"><X size={12} /></button>
                  </div>
                ))}
                <div className="flex gap-1.5">
                  <Input value={newPest} onChange={(e) => setNewPest(e.target.value)} placeholder="Pest name (termite)" className="h-9 text-sm flex-1" data-testid="tkt-ov-pest" />
                  <Input type="number" value={newAmt} onChange={(e) => setNewAmt(e.target.value)} placeholder="₹" className="h-9 text-sm w-20" data-testid="tkt-ov-amt" />
                  <Button onClick={addOverride} size="sm" variant="outline" data-testid="tkt-ov-add">Add</Button>
                </div>
              </div>
            </div>

            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2">{err}</div>}
          </>}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button onClick={onClose} variant="outline" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || loading} className="bg-indigo-700 hover:bg-indigo-800" data-testid="tkt-settings-save">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} Save Settings</Button>
        </div>
      </div>
    </div>
  );
}
